import { ChapterResponse } from '@goldcast/api/content';
import { v4 as uuidv4 } from 'uuid';
import {
  getAllCompositeSegments,
  getAllSegments,
  getClipFPS,
  isFullLengthClip,
  isSplitsInTimelineEnabledPure
} from '@goldcast/player-config/src/utils/clipUtils';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { approxGT, approxGTE, approxLT } from '@goldcast/player-config/src/utils/compareUtils';
import { CLIP_UPDATE_TYPE } from './constants';
import { getPlayerTimeFromSegmentTime, getSegmentAtCurrentFrame, getSegmentTimeFromPlayerTime } from '../timeUtils';
import {
  AddSegmentTrackAction,
  CreateHookAction,
  RemoveSegmentTrackAction,
  ReorderSegmentsAction,
  UpdateClipAction
} from './types';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

const EPSILON = 0.01;

const hasChapterAtTime = (chapterList: ChapterResponse[], time: number): boolean => {
  return chapterList.some(ch => Math.abs(ch.start_time - time) < EPSILON);
};

const getChapterActiveAtTime = (chapterList: ChapterResponse[], time: number): ChapterResponse | null => {
  const sorted = [...chapterList].sort((a, b) => a.start_time - b.start_time);
  let active: ChapterResponse | null = null;
  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i].start_time <= time + EPSILON) {
      active = sorted[i];
      continue;
    }
    break;
  }
  return active;
};

const CHAPTER_AFFECTING_ACTIONS: Set<string> = new Set([
  CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_DELETE,
  CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_DELETE_WORDS,
  CLIP_UPDATE_TYPE.UPDATE_COMPOSITE_SEGMENT_DELETE,
  CLIP_UPDATE_TYPE.REMOVE_COMPOSITE_SEGMENT_DELETE,
  CLIP_UPDATE_TYPE.RESTORE_COMPOSITE_SEGMENT_DELETE,
  CLIP_UPDATE_TYPE.BULK_PUT_COMPOSITE_SEGMENT_DELETE,
  CLIP_UPDATE_TYPE.BULK_ADD_COMPOSITE_SEGMENT_DELETES,
  CLIP_UPDATE_TYPE.UPDATE_VIDEO_SEGMENT_TRACK,
  CLIP_UPDATE_TYPE.UPDATE_SEGMENT_TRACK,
  CLIP_UPDATE_TYPE.REMOVE_SEGMENT_TRACK,
  CLIP_UPDATE_TYPE.SPLIT_SEGMENT,
  CLIP_UPDATE_TYPE.REORDER_SEGMENTS,
  CLIP_UPDATE_TYPE.ADD_SEGMENT_TRACK,
  CLIP_UPDATE_TYPE.CREATE_HOOK
]);

export const isChapterAffectingAction = (actionType: UpdateClipAction['actionType']): boolean => {
  return CHAPTER_AFFECTING_ACTIONS.has(actionType);
};

const getTotalDurationInSeconds = (clip: Clip, fps: number): number => {
  const segments = getAllSegments(clip);
  if (segments.length === 0) {
    return 0;
  }
  const lastSegment = segments[segments.length - 1];
  return lastSegment.toFrame / fps;
};

const mapOldPlayerTimeToNew = (oldPlayerTime: number, oldClip: Clip, newClip: Clip): number | null => {
  const oldSegment = getSegmentAtCurrentFrame(oldPlayerTime, oldClip);
  const currentSegmentId = oldSegment?.id;
  if (!currentSegmentId) {
    return null;
  }
  const newSegment = getAllSegments(newClip).find(s => s.id === currentSegmentId);
  if (!newSegment) {
    return oldSegment.fromFrame / getClipFPS(oldClip);
  }
  const oldSegmentTime = getSegmentTimeFromPlayerTime(oldPlayerTime, oldClip, currentSegmentId);
  const newPlayerTime = getPlayerTimeFromSegmentTime(oldSegmentTime, newClip, currentSegmentId);
  return newPlayerTime;
};

const handleInitialSplit = (
  oldClip: Clip,
  newClip: Clip,
  chapters: ChapterResponse[],
  fps: number
): ChapterResponse[] => {
  const compositeSegments = getAllCompositeSegments(oldClip);
  if (compositeSegments.length === 0) {
    return [];
  }

  const firstComposite = compositeSegments[0];

  const compositeStart = firstComposite.fromFrame / fps;
  const compositeSegmentStartTime = firstComposite.currentBounds.fromFrame / fps;
  const compositeSegmentEndTime = firstComposite.currentBounds.toFrame / fps;

  // Step 1 & 3: Filter out trimmed chapters and adjust boundary chapters
  const survivingChapters: ChapterResponse[] = [];
  for (const ch of chapters) {
    if (approxLT(ch.start_time, compositeStart)) {
      // Chapter starts before the composite range — check if it spans into the range
      // If it does (i.e. it's the last chapter before compositeStart), snap to compositeStart
      const nextChapter = chapters.find(c => c.start_time > ch.start_time);
      const chapterEndsBeyondStart = !nextChapter || nextChapter.start_time > compositeSegmentStartTime;
      if (chapterEndsBeyondStart) {
        survivingChapters.push({
          ...ch,
          start_time: getPlayerTimeFromSegmentTime(ch.start_time, oldClip, firstComposite.id)
        });
      }
      // else: entirely in trimmed section, remove
    } else if (approxGTE(ch.start_time, compositeSegmentEndTime)) {
      // Chapter in end-trimmed section → remove
      continue;
    } else {
      survivingChapters.push({
        ...ch,
        start_time: getPlayerTimeFromSegmentTime(ch.start_time, oldClip, firstComposite.id)
      });
    }
  }

  // Step 4: Handle intro/outro — check if they exist in the new segment list
  const newBounds = getAllSegments(newClip);
  const introSegment = newBounds.find(s => s.id === 'intro');
  const outroSegment = newBounds.find(s => s.id === 'outro');

  const createdChapters: { title: string; start_time: number }[] = [];

  if (introSegment) {
    createdChapters.push({ title: 'New Chapter', start_time: 0 });
  }

  // Shift all surviving chapters by the intro duration so they appear after the intro
  const updatedChapters = survivingChapters.map(ch => ({
    ...ch
  }));

  if (outroSegment) {
    const outroStart = outroSegment.fromFrame / fps;
    createdChapters.push({ title: 'New Chapter', start_time: outroStart });
  }
  createdChapters.map(ch =>
    updatedChapters.push({
      title: ch.title,
      start_time: ch.start_time,
      is_active: true,
      id: uuidv4()
    })
  );
  return updatedChapters;
};

const handleAddVideoSegment = (
  _payload: AddSegmentTrackAction,
  oldClip: Clip,
  newClip: Clip,
  chapters: ChapterResponse[],
  fps: number
): ChapterResponse[] => {
  const oldBounds = getAllSegments(oldClip);
  const newBounds = getAllSegments(newClip);
  const newSegment = newBounds.find(seg => !oldBounds.some(old => old.id === seg.id));
  if (!newSegment) {
    return [];
  }

  const insertionStartTime = newSegment.fromFrame / fps;
  const insertionEndTime = newSegment.toFrame / fps;
  const newSegmentDuration = insertionEndTime - insertionStartTime;

  const updatedChapters = chapters.map(ch => {
    if (approxGT(ch.start_time, insertionStartTime)) {
      return { ...ch, start_time: ch.start_time + newSegmentDuration };
    }
    return { ...ch };
  });

  return updatedChapters;
};

/**
 * Handles chapter adjustments when segments are reordered via drag-and-drop.
 *
 * Algorithm:
 * 1. Identify the moved segment's old boundaries (start/end) and the drop target boundary.
 * 2. Create boundary chapters at the moved segment's old start, old end, and drop target
 *    to preserve chapter coverage where the segment was removed and where it's inserted.
 * 3. Re-map all chapter start_times (both existing and newly created) from the old clip's
 *    player timeline to the new clip's timeline using segment-aware time mapping.
 */
const handleReorderSegments = (
  payload: ReorderSegmentsAction,
  oldClip: Clip,
  newClip: Clip,
  chapters: ChapterResponse[],
  fps: number
): ChapterResponse[] | null => {
  const oldBounds = getAllSegments(oldClip);

  const updatedChaptersWithoutTimeupdate: ChapterResponse[] = [];
  const updatedChaptersWithTimeupdate: ChapterResponse[] = [];
  const createdChapters: { title: string; start_time: number }[] = [];

  const tryCreateBoundaryChapter = (time: number, title: string) => {
    const pending = [...createdChapters.map(ch => ({ ...ch }) as ChapterResponse), ...chapters];
    if (!hasChapterAtTime(pending, time)) {
      createdChapters.push({ title, start_time: time });
    }
  };

  const movedSegOld = oldBounds.find(s => s.id === payload.activeId);
  const movedSegNew = oldBounds.find(s => s.id === payload.overId);

  if (!movedSegOld || !movedSegNew) {
    return null;
  }
  const movedSegOldStart = movedSegOld.fromFrame / fps;
  const movedSegOldEnd = movedSegOld.toFrame / fps;
  const movedSegNewStart = movedSegNew.fromFrame / fps;
  tryCreateBoundaryChapter(
    movedSegOldStart,
    getChapterActiveAtTime(chapters, movedSegOldStart)?.title || 'New Chapter'
  );
  tryCreateBoundaryChapter(movedSegOldEnd, getChapterActiveAtTime(chapters, movedSegOldEnd)?.title || 'New Chapter');
  tryCreateBoundaryChapter(
    movedSegNewStart,
    getChapterActiveAtTime(chapters, movedSegNewStart)?.title || 'New Chapter'
  );

  updatedChaptersWithoutTimeupdate.push(
    ...createdChapters.map(ch => ({
      title: ch.title,
      start_time: ch.start_time,
      is_active: true,
      id: uuidv4()
    }))
  );

  for (const ch of [...updatedChaptersWithoutTimeupdate, ...chapters]) {
    const newTime = mapOldPlayerTimeToNew(ch.start_time, oldClip, newClip);
    if (newTime !== null) {
      updatedChaptersWithTimeupdate.push({ ...ch, start_time: newTime });
    }
  }

  return updatedChaptersWithTimeupdate;
};

const handleCreateHook = (payload: CreateHookAction, oldClip: Clip, chapters: ChapterResponse[]): ChapterResponse[] => {
  const { startTime, endTime, segmentId } = payload;

  const playerStartTime = getPlayerTimeFromSegmentTime(startTime, oldClip, segmentId);
  const playerEndTime = getPlayerTimeFromSegmentTime(endTime, oldClip, segmentId);
  const hookDuration = playerEndTime - playerStartTime;

  if (hookDuration <= 0) {
    return chapters;
  }

  const firstChapter = [...chapters].sort((a, b) => a.start_time - b.start_time)[0];

  const shiftedChapters = chapters.map(ch => ({
    ...ch,
    start_time: ch.id === firstChapter?.id ? 0 : ch.start_time + hookDuration
  }));

  return shiftedChapters;
};

const handleDeleteChanges = (
  payload: UpdateClipAction,
  oldClip: Clip,
  newClip: Clip,
  chapters: ChapterResponse[]
): ChapterResponse[] | null => {
  const oldSegments = getAllSegments(oldClip);
  const remainingSegmentIds = new Set(getAllSegments(newClip).map(segment => segment.id));

  const getLastNonDeletedSegmentId = (currentSegmentId: string): string | null => {
    const currentSegmentIndex = oldSegments.findIndex(segment => segment.id === currentSegmentId);
    if (currentSegmentIndex <= 0) {
      return null;
    }

    for (let i = currentSegmentIndex - 1; i >= 0; i--) {
      if (remainingSegmentIds.has(oldSegments[i].id)) {
        return oldSegments[i].id;
      }
    }

    return null;
  };

  const getUpdatedChapterStartTime = (chapter: ChapterResponse): number => {
    const currentSegment = getSegmentAtCurrentFrame(chapter.start_time, oldClip);
    if (!currentSegment) {
      return chapter.start_time;
    }

    const segmentTime = getSegmentTimeFromPlayerTime(chapter.start_time, oldClip, currentSegment.id);

    if (remainingSegmentIds.has(currentSegment.id)) {
      const mappedTime = getPlayerTimeFromSegmentTime(segmentTime, newClip, currentSegment.id);
      return mappedTime ?? chapter.start_time;
    }

    const fallbackSegmentId = getLastNonDeletedSegmentId(currentSegment.id);
    if (!fallbackSegmentId) {
      return 0;
    }

    const mappedTime = getPlayerTimeFromSegmentTime(segmentTime, newClip, fallbackSegmentId);
    return mappedTime ?? chapter.start_time;
  };

  const updatedChapters = chapters.map(ch => ({
    ...ch,
    start_time: getUpdatedChapterStartTime(ch)
  }));
  return updatedChapters;
};

const handleRemoveSegment = (
  payload: RemoveSegmentTrackAction,
  oldClip: Clip,
  newClip: Clip,
  chapters: ChapterResponse[],
  fps: number
): ChapterResponse[] | null => {
  const isSplitsEnabledAfterDelete = isSplitsInTimelineEnabledPure({
    clip: newClip,
    isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
  });
  const isSplitsEnabledBeforeDelete = isSplitsInTimelineEnabledPure({
    clip: oldClip,
    isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
  });
  if (!isSplitsEnabledAfterDelete && isSplitsEnabledBeforeDelete) {
    const firstCompositeSegmentId = getAllCompositeSegments(newClip)[0]?.id;
    const firstComposite = getAllCompositeSegments(oldClip).find(c => c.id !== payload.id);
    const firstCompositeStart = firstComposite ? firstComposite.fromFrame / fps : 0;
    const firstCompositeEnd = firstComposite ? firstComposite.toFrame / fps : 0;
    const updateChapters: ChapterResponse[] = [];
    chapters.forEach((ch, index) => {
      const nextChapter = chapters[index + 1];
      if (ch.start_time < firstCompositeStart && (!nextChapter || firstCompositeStart < nextChapter.start_time)) {
        updateChapters.push({ ...ch, start_time: firstCompositeStart });
      } else if (ch.start_time >= firstCompositeStart && ch.start_time < firstCompositeEnd) {
        updateChapters.push({ ...ch, start_time: ch.start_time });
      }
    });

    const updatedChapterWithTimeupdate = updateChapters.map(ch => ({
      ...ch,
      start_time: getSegmentTimeFromPlayerTime(ch.start_time, newClip, firstCompositeSegmentId!)
    }));
    if (updatedChapterWithTimeupdate.length !== 0) {
      updatedChapterWithTimeupdate[0].start_time = 0;
    }
    return updatedChapterWithTimeupdate.sort((a, b) => a.start_time - b.start_time);
  }

  return handleDeleteChanges(payload, oldClip, newClip, chapters);
};

const cleanupChaptersBeyondClipEnd = (chapters: ChapterResponse[], clip: Clip, fps: number): ChapterResponse[] => {
  const totalDuration = getTotalDurationInSeconds(clip, fps);
  if (totalDuration <= 0) {
    return chapters;
  }
  const dedupedAndSortedChapters = chapters
    .map(ch => ({ ...ch, start_time: ch.start_time < 0 ? 0 : ch.start_time }))
    .sort((a, b) => a.start_time - b.start_time)
    .reduce<ChapterResponse[]>((acc, chapter) => {
      if (acc.length === 0) {
        acc.push(chapter);
        return acc;
      }

      if (chapter.start_time < 0) {
        chapter.start_time = 0;
      }

      const last = acc[acc.length - 1];
      if (Math.abs(last.start_time - chapter.start_time) < EPSILON) {
        acc[acc.length - 1] = chapter;
        return acc;
      }

      acc.push(chapter);
      return acc;
    }, []);

  return dedupedAndSortedChapters
    .filter(ch => ch.start_time < totalDuration)
    .sort((a, b) => a.start_time - b.start_time);
};

export const computeChapterAdjustments = ({
  actionType,
  updateClipPayload,
  oldClip,
  newClip,
  chapters
}: {
  actionType: UpdateClipAction['actionType'];
  updateClipPayload: UpdateClipAction;
  oldClip: Clip;
  newClip: Clip;
  chapters: ChapterResponse[];
}): ChapterResponse[] | null => {
  if (!chapters || chapters.length === 0) {
    return null;
  }
  // Chapters are only supported on the full recording, not on clips. Never adjust chapters
  // when the edit targets a clip.
  if (!isFullLengthClip(newClip)) {
    return null;
  }
  const fps = getClipFPS(oldClip);

  let result: ChapterResponse[] | null = null;
  const isTransitioningToSplits =
    !isSplitsInTimelineEnabledPure({
      clip: oldClip,
      isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
    }) &&
    isSplitsInTimelineEnabledPure({
      clip: newClip,
      isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
    });
  switch (actionType) {
    case CLIP_UPDATE_TYPE.ADD_SEGMENT_TRACK:
      if (isTransitioningToSplits) {
        result = handleInitialSplit(oldClip, newClip, chapters, fps);
      }
      result = handleAddVideoSegment(
        updateClipPayload as AddSegmentTrackAction,
        oldClip,
        newClip,
        result || chapters,
        fps
      );
      break;
    case CLIP_UPDATE_TYPE.REORDER_SEGMENTS:
      result = handleReorderSegments(updateClipPayload as ReorderSegmentsAction, oldClip, newClip, chapters, fps);
      break;
    case CLIP_UPDATE_TYPE.SPLIT_SEGMENT:
      if (isTransitioningToSplits) {
        result = handleInitialSplit(oldClip, newClip, chapters, fps);
      }
      break;
    case CLIP_UPDATE_TYPE.CREATE_HOOK:
      result = handleCreateHook(updateClipPayload as CreateHookAction, oldClip, chapters);
      break;
    case CLIP_UPDATE_TYPE.REMOVE_SEGMENT_TRACK:
      result = handleRemoveSegment(updateClipPayload as RemoveSegmentTrackAction, oldClip, newClip, chapters, fps);
      break;
    case CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_DELETE:
    case CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_DELETE_WORDS:
    case CLIP_UPDATE_TYPE.UPDATE_COMPOSITE_SEGMENT_DELETE:
    case CLIP_UPDATE_TYPE.REMOVE_COMPOSITE_SEGMENT_DELETE:
    case CLIP_UPDATE_TYPE.RESTORE_COMPOSITE_SEGMENT_DELETE:
    case CLIP_UPDATE_TYPE.BULK_PUT_COMPOSITE_SEGMENT_DELETE:
    case CLIP_UPDATE_TYPE.BULK_ADD_COMPOSITE_SEGMENT_DELETES:
    case CLIP_UPDATE_TYPE.UPDATE_VIDEO_SEGMENT_TRACK:
    case CLIP_UPDATE_TYPE.UPDATE_SEGMENT_TRACK:
      result = handleDeleteChanges(updateClipPayload, oldClip, newClip, chapters);
      break;
    default:
      return null;
  }

  if (!result) {
    return null;
  }

  return cleanupChaptersBeyondClipEnd(result, newClip, fps);
};
